import React from "react";
import { Tag } from "primereact/tag";

import { ColumnConfig } from "../../../components/ReusableList";
import { ProspectSummary } from "../../../types/onboarding";
import { stateLabel, stateSeverity } from "../../../utils/onboardingStates";
import {
  availabilityProgress,
  readinessLabel,
  readinessSeverity,
  readyDuration,
} from "../../../utils/stationReadiness";
import {
  needsWallboxClarification,
  stationCountLabel,
  stationCounts,
} from "../../../utils/stationCounts";
import { setupFeeStatusLabel } from "../../../utils/setupFee";
import { stationIdsLabel } from "../../../utils/stationIds";
import ProspectTypeTag from "./components/ProspectTypeTag";

// NOTE: this config intentionally contains NO sensitive fields (IBAN, BIC,
// account holder, bank name, VAT) — those never appear in the list (FR-005/SC-004).

const formatDate = (iso?: string): string =>
  iso ? new Date(iso).toLocaleDateString() : "";

/** What the list's search box matches: the customer's names, company and station IDs (CWA-208). */
export const prospectSearchText = (row: ProspectSummary): string =>
  [row.first_name, row.last_name, row.company_name, ...(row.technical_names ?? [])].filter(Boolean).join(" ");

// Several IDs open to the full list; opening it must not open the prospect.
const StationIds: React.FC<{ ids: string[] }> = ({ ids }) => (
  <details onClick={(e) => e.stopPropagation()}>
    <summary>{stationIdsLabel(ids)}</summary>
    <ul className="m-0 pl-3" aria-label="Station IDs">
      {ids.map((id) => (
        <li key={id}>{id}</li>
      ))}
    </ul>
  </details>
);

const StationsCell: React.FC<{ row: ProspectSummary }> = ({ row }) => {
  const ids = row.technical_names ?? [];
  const counts = stationCounts(row);
  if (ids.length === 0) return <>{stationCountLabel(counts)}</>;
  const showCount = ids.length > 1 || (counts.chargePoints > 0 && counts.chargePoints !== counts.stations);
  return (
    <div className="flex flex-column gap-1">
      {ids.length === 1 ? <span>{ids[0]}</span> : <StationIds ids={ids} />}
      {showCount && <span className="text-500 text-sm">{stationCountLabel(counts)}</span>}
    </div>
  );
};

export const prospectColumns: ColumnConfig[] = [
  {
    label: "Name",
    value: "last_name",
    sortable: true,
    body: (row: ProspectSummary) => `${row.first_name} ${row.last_name}`.trim(),
  },
  {
    // The column agents scan the list for, so it sits next to the name and
    // renders heavier than the supporting tags, with the colour carrying the
    // in-flight / waiting / live / gone distinction (see stateSeverity).
    label: "Status",
    value: "current_state",
    sortable: true,
    bodyClassName: "prospect-status-cell",
    body: (row: ProspectSummary) => (
      <Tag
        severity={stateSeverity(row.current_state)}
        value={stateLabel(row.current_state)}
      />
    ),
  },
  {
    label: "Type",
    value: "customer_type",
    sortable: true,
    body: (row: ProspectSummary) =>
      row.customer_type === "BUSINESS"
        ? `Business${row.company_name ? ` — ${row.company_name}` : ""}`
        : "Private",
  },
  {
    label: "Submitted",
    value: "submitted_at",
    sortable: true,
    body: (row: ProspectSummary) => formatDate(row.submitted_at),
  },
  {
    // Wallboxes and charging points are two different facts (CWA-154 finding 1):
    // one wallbox can provide several charging points, and eRound registers the
    // wallbox. Sorted on the wallbox count, since that is what drives how many
    // stations get registered.
    label: "Stations",
    value: "station_count",
    sortable: true,
    body: (row: ProspectSummary) => <StationsCell row={row} />,
  },
  {
    // Readiness is derived by the backend from an HOURLY sync, so this is
    // deliberately worded as elapsed time rather than a current state, and the
    // progress is shown so a half-installed customer does not look like one who
    // has not started (CWA-154 findings 5+6).
    label: "Stations ready",
    value: "ready_to_publish_since",
    sortable: true,
    body: (row: ProspectSummary) => {
      const label = readinessLabel(row.station_readiness);
      if (!label) return "—";
      const progress = availabilityProgress(row);
      const waiting =
        row.station_readiness === "READY_TO_PUBLISH"
          ? readyDuration(row.ready_to_publish_since)
          : null;
      return (
        <div className="flex flex-column gap-1">
          <Tag severity={readinessSeverity(row.station_readiness)} value={label} />
          {progress && (
            <span className="text-500 text-sm">{progress.label}</span>
          )}
          {waiting && <span className="text-500 text-sm">{waiting}</span>}
        </div>
      );
    },
  },
  {
    label: "Setup fee",
    value: "setup_fee_status",
    sortable: true,
    body: (row: ProspectSummary) =>
      row.setup_fee_status ? setupFeeStatusLabel(row.setup_fee_status) : "—",
  },
  {
    label: "Flags",
    value: "flags",
    body: (row: ProspectSummary) => (
      <div className="flex gap-1 flex-wrap">
        {row.needs_follow_up && (
          <Tag
            severity="danger"
            value="Needs follow-up"
            title="The reminders went unanswered or support's step is overdue — call the customer or pick it up."
          />
        )}
        {needsWallboxClarification(row) && (
          <Tag
            severity="warning"
            value="Multiple wallboxes"
            title="This customer has more than one wallbox — check the setup before sending the configuration."
          />
        )}
        {row.more_info_needed && (
          <Tag severity="info" value="More info needed" />
        )}
        <ProspectTypeTag prospect={row} />
      </div>
    ),
  },
];
